/**
 * Brand Voice Manager Component
 * Admin interface for managing multiple brand voices
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import brandVoiceStorageService from '../services/BrandVoiceStorageService';
import type { BrandVoice } from '../types/brandVoice';
import { alertService } from './Alert';
import { confirmDialogService } from './ui/ConfirmDialog';
import logger from '../utils/logger';
import './BrandVoiceManager.css';

interface BrandVoiceManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectBrandVoice?: (brandVoice: BrandVoice) => void;
  currentBrandVoiceId?: string;
}

const BrandVoiceManager: React.FC<BrandVoiceManagerProps> = ({
  isOpen,
  onClose,
  onSelectBrandVoice,
  currentBrandVoiceId
}) => {
  const [brandVoices, setBrandVoices] = useState<BrandVoice[]>([]);
  const [defaultBrandVoiceId, setDefaultBrandVoiceId] = useState<string | null>(null);
  const [editingVoice, setEditingVoice] = useState<BrandVoice | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [previewVoice, setPreviewVoice] = useState<BrandVoice | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formInstructions, setFormInstructions] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState('#3b82f6');
  const [formIcon, setFormIcon] = useState('🎯');

  useEffect(() => {
    if (isOpen) {
      loadBrandVoices();
    }
  }, [isOpen]);

  const loadBrandVoices = async () => {
    try {
      console.log('[BrandVoiceManager] Loading brand voices...');
      const voices = await brandVoiceStorageService.listBrandVoices();
      console.log('[BrandVoiceManager] Loaded brand voices:', voices);
      setBrandVoices(voices);

      const defaultVoice = await brandVoiceStorageService.getDefaultBrandVoice();
      console.log('[BrandVoiceManager] Default voice:', defaultVoice);
      setDefaultBrandVoiceId(defaultVoice?.id || null);
    } catch (error) {
      console.error('[BrandVoiceManager] Failed to load brand voices:', error);
      logger.error('Failed to load brand voices:', error);
      alertService.error('Failed to load brand voices');
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingVoice(null);
    setFormName('');
    setFormInstructions('');
    setFormDescription('');
    setFormColor('#3b82f6');
    setFormIcon('');
  };

  const handleEdit = (voice: BrandVoice) => {
    setEditingVoice(voice);
    setIsCreating(false);
    setFormName(voice.name);
    setFormInstructions(voice.instructions);
    setFormDescription(voice.description || '');
    setFormColor(voice.color || '#3b82f6');
    setFormIcon(voice.icon || '');
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      alertService.warning('Please enter a name for the brand voice');
      return;
    }

    if (!formInstructions.trim()) {
      alertService.warning('Please enter instructions for the brand voice');
      return;
    }

    try {
      const voice: BrandVoice = {
        id: editingVoice?.id || `brand_voice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: formName.trim(),
        instructions: formInstructions.trim(),
        description: formDescription.trim() || undefined,
        color: formColor,
        icon: formIcon,
        createdAt: editingVoice?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await brandVoiceStorageService.saveBrandVoice(voice);
      
      // If this is the first brand voice, set it as default
      if (brandVoices.length === 0) {
        await brandVoiceStorageService.setDefaultBrandVoice(voice.id);
      }

      alertService.success(`Brand voice "${voice.name}" saved successfully`);
      
      await loadBrandVoices();
      setIsCreating(false);
      setEditingVoice(null);
    } catch (error) {
      logger.error('Failed to save brand voice:', error);
      alertService.error('Failed to save brand voice');
    }
  };

  const handleDelete = async (voice: BrandVoice) => {
    const confirmed = await confirmDialogService.confirm({
      title: 'Delete Brand Voice?',
      message: `Are you sure you want to delete "${voice.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      await brandVoiceStorageService.deleteBrandVoice(voice.id);
      alertService.success(`Brand voice "${voice.name}" deleted`);
      await loadBrandVoices();
      
      if (editingVoice?.id === voice.id) {
        setEditingVoice(null);
        setIsCreating(false);
      }
    } catch (error) {
      logger.error('Failed to delete brand voice:', error);
      alertService.error('Failed to delete brand voice');
    }
  };

  const handleSetDefault = async (voice: BrandVoice) => {
    try {
      await brandVoiceStorageService.setDefaultBrandVoice(voice.id);
      alertService.success(`"${voice.name}" set as default brand voice`);
      await loadBrandVoices();
    } catch (error) {
      logger.error('Failed to set default brand voice:', error);
      alertService.error('Failed to set default brand voice');
    }
  };

  const handleSelect = (voice: BrandVoice) => {
    if (onSelectBrandVoice) {
      onSelectBrandVoice(voice);
      onClose();
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingVoice(null);
  };

  if (!isOpen) return null;

  const showingForm = isCreating || editingVoice !== null;

  return ReactDOM.createPortal(
    <div className="brand-voice-manager-overlay" onClick={onClose}>
      <div className="brand-voice-manager-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="brand-voice-manager-header">
          <h2>Brand Voice Manager</h2>
          <button className="close-btn" onClick={onClose} title="Close">
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="brand-voice-manager-content">
          {/* Left Panel - List */}
          <div className="brand-voice-list-panel">
            <div className="brand-voice-list-header">
              <h3>Brand Voices ({brandVoices.length})</h3>
              <button className="create-btn" onClick={handleCreate} title="Create new brand voice">
                <AddIcon fontSize="small" /> New
              </button>
            </div>

            <div className="brand-voice-list">
              {brandVoices.length === 0 ? (
                <div className="empty-state">
                  <p>No brand voices yet</p>
                  <p className="empty-hint">Create your first brand voice to get started</p>
                </div>
              ) : (
                brandVoices.map(voice => (
                  <div
                    key={voice.id}
                    className={`brand-voice-item ${currentBrandVoiceId === voice.id ? 'active-voice' : ''} ${editingVoice?.id === voice.id ? 'editing' : ''} ${previewVoice?.id === voice.id ? 'previewing' : ''}`}
                    onClick={() => setPreviewVoice(voice)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="brand-voice-item-header">
                      <div className="brand-voice-item-info">
                        <span className="brand-voice-icon" style={{ 
                          backgroundColor: voice.color || '#3b82f6',
                          color: 'white',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '6px',
                          fontWeight: '600',
                          fontSize: '14px',
                          flexShrink: 0
                        }}>
                          {voice.name.substring(0, 2).toUpperCase()}
                        </span>
                        <div className="brand-voice-item-details">
                          <div className="brand-voice-name">
                          {voice.name}
                          {currentBrandVoiceId === voice.id && (
                            <span className="voice-badge active-badge">Active</span>
                          )}
                          {previewVoice?.id === voice.id && (
                            <span className="voice-badge preview-badge">Previewing</span>
                          )}
                        </div>
                          {voice.description && (
                            <div className="brand-voice-description">{voice.description}</div>
                          )}
                        </div>
                      </div>
                      <div className="brand-voice-item-actions">
                        <button
                          className={`default-btn ${defaultBrandVoiceId === voice.id ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefault(voice);
                          }}
                          title={defaultBrandVoiceId === voice.id ? 'Default' : 'Set as default'}
                        >
                          {defaultBrandVoiceId === voice.id ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                        </button>
                        <button className="edit-btn" onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(voice);
                        }} title="Edit">
                          <EditIcon fontSize="small" />
                        </button>
                        <button className="delete-btn" onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(voice);
                        }} title="Delete">
                          <DeleteIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                    <div className="brand-voice-item-footer">
                      {onSelectBrandVoice && (
                        <button 
                          className="select-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(voice);
                          }}
                        >
                          {currentBrandVoiceId === voice.id ? 'Selected' : 'Use This'}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Form or Preview */}
          <div className="brand-voice-detail-panel">
            {showingForm ? (
              <>
                <div className="brand-voice-form-header">
                  <h3>{isCreating ? 'Create Brand Voice' : 'Edit Brand Voice'}</h3>
                </div>
                <div className="brand-voice-form">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="e.g., Professional, Casual, Technical"
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      placeholder="Brief description of this brand voice"
                    />
                  </div>

                  <div className="form-group">
                    <label>Color</label>
                    <input
                      type="color"
                      value={formColor}
                      onChange={(e) => setFormColor(e.target.value)}
                    />
                    <div className="form-hint">
                      The first 2 letters of the name will be used as initials
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Brand Instructions *</label>
                    <textarea
                      value={formInstructions}
                      onChange={(e) => setFormInstructions(e.target.value)}
                      placeholder="Enter your brand voice guidelines, tone preferences, writing style, and any system-wide instructions..."
                      rows={12}
                    />
                    <div className="form-hint">
                      {formInstructions.length} characters
                    </div>
                  </div>

                  <div className="form-actions">
                    <button className="cancel-btn" onClick={handleCancel}>
                      Cancel
                    </button>
                    <button className="save-btn" onClick={handleSave}>
                      {isCreating ? 'Create' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </>
            ) : previewVoice ? (
              <>
                <div className="brand-voice-preview-header">
                  <h3>
                    <span style={{ 
                      backgroundColor: previewVoice.color || '#3b82f6',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontWeight: '600',
                      fontSize: '14px',
                      marginRight: '8px'
                    }}>
                      {previewVoice.name.substring(0, 2).toUpperCase()}
                    </span>
                    {previewVoice.name}
                  </h3>
                  <button className="close-preview-btn" onClick={() => setPreviewVoice(null)}>
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
                <div className="brand-voice-preview">
                  {previewVoice.description && (
                    <div className="preview-description">{previewVoice.description}</div>
                  )}
                  <div className="preview-instructions">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {previewVoice.instructions}
                    </ReactMarkdown>
                  </div>
                  <div className="preview-metadata">
                    <div>Created: {new Date(previewVoice.createdAt).toLocaleDateString()}</div>
                    <div>Updated: {new Date(previewVoice.updatedAt).toLocaleDateString()}</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="brand-voice-detail-empty">
                <p>Select a brand voice to preview or click "New" to create one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BrandVoiceManager;
